import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import DataTable, { Pagination } from "../../components/admin/DataTable";

const ROLES = [
  { value: "", label: "All Roles" },
  { value: "customer", label: "Customers" },
  { value: "seller", label: "Sellers" },
  { value: "admin", label: "Admins" },
];

export default function AdminUsers() {
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [blocked, setBlocked] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (role) params.role = role;
      if (q) params.q = q;
      if (blocked !== "") params.is_blocked = blocked === "true";
      const { data } = await api.get("/admin/users", { params });
      setData(data);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role, blocked, page]);

  const toggleBlock = async (u) => {
    const next = !u.is_blocked;
    const reason = next ? window.prompt("Reason for blocking?") : null;
    if (next && reason === null) return;
    try {
      await api.put(`/admin/users/${u.id}/block`, { is_blocked: next, reason });
      toast.success(next ? "User blocked" : "User unblocked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const columns = [
    { key: "name", label: "Name", render: (r) => r.name || "—" },
    { key: "phone", label: "Phone" },
    { key: "email", label: "Email", render: (r) => r.email || "—" },
    { key: "role", label: "Role", render: (r) => <span className="uppercase text-xs">{r.role}</span> },
    {
      key: "status", label: "Status",
      render: (r) =>
        r.is_blocked ? (
          <span className="text-red-700 text-xs uppercase">Blocked</span>
        ) : (
          <span className="text-green-700 text-xs uppercase">Active</span>
        ),
    },
    { key: "created_at", label: "Joined", render: (r) => (r.created_at || "").slice(0, 10) },
    {
      key: "actions", label: "",
      render: (r) =>
        r.role === "admin" ? null : (
          <button
            onClick={() => toggleBlock(r)}
            className="kb-btn-outline !py-2 !px-3 !text-xs"
            data-testid={`toggle-block-${r.phone}`}
          >
            {r.is_blocked ? "Unblock" : "Block"}
          </button>
        ),
    },
  ];

  return (
    <div data-testid="admin-users-page">
      <div className="overline mb-2">People</div>
      <h1 className="font-display text-4xl mb-8">Users</h1>

      <div className="bg-white border border-[hsl(var(--kb-border))] p-4 mb-4 grid md:grid-cols-4 gap-3">
        <select className="kb-input" value={role} onChange={(e) => { setRole(e.target.value); setPage(1); }} data-testid="filter-role">
          {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <select className="kb-input" value={blocked} onChange={(e) => { setBlocked(e.target.value); setPage(1); }} data-testid="filter-blocked">
          <option value="">All</option>
          <option value="false">Active only</option>
          <option value="true">Blocked only</option>
        </select>
        <input
          className="kb-input md:col-span-2"
          placeholder="Search name / phone / email"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())}
          data-testid="filter-search"
        />
      </div>

      <DataTable columns={columns} rows={data.items} loading={loading} emptyLabel="No users match" testid="users-table" />
      <Pagination page={page} limit={20} total={data.total} onPage={setPage} />
    </div>
  );
}
